import { useState, useEffect } from 'react';
import { colors, mono, sans, thStyle, tdStyle, statusStyle } from './theme';
import { StatCard } from './components/StatCard';
import { BacktestView } from './components/BacktestView';

const API_BASE = 'https://btc-signal-api-prod.ppicwkb.workers.dev';
const SIGNALS_PAGE_SIZE = 20;

interface Pagination {
  page: number;
  limit: number;
  total: number;
}

interface Signal {
  id: number;
  timeframe: string;
  type: string;
  entryZoneStart: number;
  entryZoneEnd: number;
  stopLoss: number;
  tp1: number;
  tp2: number;
  score: number;
  status: string;
  candleOpenTime: number;
  createdAt: string;
}

interface Stats {
  total: number;
  active: number;
  tp1Hits: number;
  accuracy: string;
}

interface LogsInfo {
  total: number;
  olderThanDays: number;
  eligibleForCleanup: number;
  oldestTimestamp: number | null;
}

function App() {
  const [signals, setSignals] = useState<Signal[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [logsInfo, setLogsInfo] = useState<LogsInfo | null>(null);
  const [cleaningUp, setCleaningUp] = useState(false);
  const [cleanupMessage, setCleanupMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // `token`: the authenticated token that gates the view and is sent to the
  // API. Only ever set from localStorage on mount or after a real form
  // submit -- never from the input's onChange directly.
  const [token, setToken] = useState('');
  // `tokenInput`: just the login field's live text. Browser autofill can
  // freely populate this without it counting as "logged in". Previously
  // the login field was bound directly to `token`, so autofill alone (no
  // submit) flipped the app into the main view without ever calling
  // fetchData() -- that's why the dashboard showed empty with no error.
  const [tokenInput, setTokenInput] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [view, setView] = useState<'signals' | 'backtest'>('signals');
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [signalsLoading, setSignalsLoading] = useState(false);

  useEffect(() => {
    const storedToken = localStorage.getItem('dashboard_token');
    if (storedToken) {
      setToken(storedToken);
      fetchData(storedToken);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchData = async (authToken: string) => {
    setLoading(true);
    setErrorMessage(null);
    try {
      const [signalsRes, statsRes, logsRes] = await Promise.all([
        fetch(`${API_BASE}/api/signals?page=1&limit=${SIGNALS_PAGE_SIZE}`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        }),
        fetch(`${API_BASE}/api/stats`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        }),
        fetch(`${API_BASE}/api/admin/logs`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        })
      ]);

      if (signalsRes.status === 401 || statsRes.status === 401) {
        setErrorMessage('Token ditolak oleh server (401) -- token di perangkat ini tidak cocok dengan DASHBOARD_ACCESS_TOKEN. Silakan login ulang.');
        localStorage.removeItem('dashboard_token');
        setToken('');
        return;
      }

      if (!signalsRes.ok || !statsRes.ok) {
        setErrorMessage(`API merespons tapi gagal -- /api/signals: ${signalsRes.status}, /api/stats: ${statsRes.status}.`);
        return;
      }

      const signalsData = await signalsRes.json();
      const statsData = await statsRes.json();
      setSignals(signalsData.signals || []);
      setPagination(signalsData.pagination ?? null);
      setStats(statsData);

      // Logs panel is a secondary feature -- a failure here shouldn't block
      // the main dashboard from rendering.
      if (logsRes.ok) {
        setLogsInfo(await logsRes.json());
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setErrorMessage(`Tidak bisa menghubungi API (kemungkinan CORS/jaringan): ${message}`);
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Refetches just /api/signals for a given page -- deliberately separate
  // from fetchData() so clicking Prev/Next doesn't re-trigger the full
  // Loading... screen or re-fetch stats/logs that haven't changed.
  const changeSignalsPage = async (newPage: number) => {
    if (newPage < 1) return;
    setSignalsLoading(true);
    setErrorMessage(null);
    try {
      const res = await fetch(`${API_BASE}/api/signals?page=${newPage}&limit=${SIGNALS_PAGE_SIZE}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.status === 401) {
        setErrorMessage('Token ditolak oleh server (401) -- token di perangkat ini tidak cocok dengan DASHBOARD_ACCESS_TOKEN. Silakan login ulang.');
        localStorage.removeItem('dashboard_token');
        setToken('');
        return;
      }
      if (!res.ok) {
        setErrorMessage(`API merespons tapi gagal -- /api/signals: ${res.status}.`);
        return;
      }
      const data = await res.json();
      setSignals(data.signals || []);
      setPagination(data.pagination ?? null);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setErrorMessage(`Tidak bisa menghubungi API (kemungkinan CORS/jaringan): ${message}`);
    } finally {
      setSignalsLoading(false);
    }
  };

  const handleCleanupLogs = async () => {
    if (!logsInfo || logsInfo.eligibleForCleanup === 0) return;
    const confirmed = window.confirm(
      `Hapus ${logsInfo.eligibleForCleanup} baris error log yang lebih tua dari ${logsInfo.olderThanDays} hari? Tindakan ini tidak bisa dibatalkan.`
    );
    if (!confirmed) return;

    setCleaningUp(true);
    setCleanupMessage(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/logs/cleanup`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ olderThanDays: logsInfo.olderThanDays }),
      });
      if (!res.ok) {
        setCleanupMessage(`Gagal hapus log (${res.status}).`);
        return;
      }
      const data = await res.json();
      setCleanupMessage(`${data.deleted} baris log lama terhapus. Sisa: ${data.remaining}.`);
      setLogsInfo((prev) => prev ? { ...prev, total: data.remaining, eligibleForCleanup: 0 } : prev);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setCleanupMessage(`Gagal menghubungi API: ${message}`);
    } finally {
      setCleaningUp(false);
    }
  };

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = tokenInput.trim();
    if (trimmed) {
      localStorage.setItem('dashboard_token', trimmed);
      setToken(trimmed);
      fetchData(trimmed);
    }
  };

  if (!token) {
    return (
      <div style={{ minHeight: '100vh', background: colors.bg, fontFamily: sans, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ padding: '32px', width: '100%', maxWidth: '380px', background: colors.panel, border: `1px solid ${colors.border}`, borderRadius: '10px' }}>
          <h1 style={{ color: colors.text, fontSize: '20px', margin: '0 0 20px' }}>BTC Signal Dashboard</h1>
          {errorMessage && (
            <div style={{ color: colors.short, background: colors.dangerBg, padding: '10px 12px', borderRadius: '6px', marginBottom: '16px', fontSize: '13px', lineHeight: 1.5 }}>
              {errorMessage}
            </div>
          )}
          <form onSubmit={handleLogin}>
            <input
              type="password"
              placeholder="Enter access token"
              value={tokenInput}
              onChange={(e) => setTokenInput(e.target.value)}
              autoComplete="off"
              style={{
                width: '100%', padding: '10px 12px', marginBottom: '12px', boxSizing: 'border-box',
                background: colors.panelAlt, border: `1px solid ${colors.border}`, borderRadius: '6px',
                color: colors.text, fontSize: '14px', outline: 'none',
              }}
            />
            <button type="submit" style={{
              width: '100%', padding: '10px', background: colors.accent, color: '#0b0f17',
              border: 'none', borderRadius: '6px', fontWeight: 600, fontSize: '14px', cursor: 'pointer',
            }}>
              Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: colors.bg, color: colors.textDim, fontFamily: sans, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        Loading...
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: colors.bg, fontFamily: sans, color: colors.text, padding: '24px' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <h1 style={{ fontSize: '22px', margin: '0 0 24px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span>BTC Signal Dashboard</span>
          <span style={{ color: '#f7931a' }}>&#8383;</span>
        </h1>

        <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
          <button
            onClick={() => setView('signals')}
            style={{
              padding: '8px 16px', borderRadius: '6px', border: `1px solid ${colors.border}`,
              background: view === 'signals' ? colors.accent : 'transparent',
              color: view === 'signals' ? '#0b0f17' : colors.textDim,
              fontWeight: 600, fontSize: '13px', cursor: 'pointer',
            }}
          >
            Signals
          </button>
          <button
            onClick={() => setView('backtest')}
            style={{
              padding: '8px 16px', borderRadius: '6px', border: `1px solid ${colors.border}`,
              background: view === 'backtest' ? colors.accent : 'transparent',
              color: view === 'backtest' ? '#0b0f17' : colors.textDim,
              fontWeight: 600, fontSize: '13px', cursor: 'pointer',
            }}
          >
            Backtest
          </button>
        </div>

        {view === 'backtest' ? (
          <BacktestView />
        ) : (
          <>
            {errorMessage && (
              <div style={{ color: colors.short, background: colors.dangerBg, padding: '12px 14px', borderRadius: '8px', marginBottom: '20px', fontSize: '14px' }}>
                {errorMessage}
                {' '}
                <button onClick={() => fetchData(token)} style={{
                  marginLeft: '10px', background: 'transparent', border: `1px solid ${colors.short}`,
                  color: colors.short, borderRadius: '5px', padding: '4px 10px', cursor: 'pointer', fontSize: '13px',
                }}>
                  Coba lagi
                </button>
              </div>
            )}

            {stats && (
              <div style={{ display: 'flex', gap: '16px', marginBottom: '28px', flexWrap: 'wrap' }}>
                <StatCard title="Total Signals" value={stats.total} />
                <StatCard title="Active" value={stats.active} valueColor="#7dd3fc" />
                <StatCard title="TP1 Hits" value={stats.tp1Hits} valueColor={colors.long} />
                <StatCard title="Accuracy" value={stats.accuracy} valueColor={colors.long} />
              </div>
            )}

            {logsInfo && (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px',
                padding: '14px 18px', background: colors.panel, border: `1px solid ${colors.border}`,
                borderRadius: '10px', marginBottom: '28px',
              }}>
                <div>
                  <div style={{ fontSize: '12px', color: colors.textDim, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' }}>
                    Error Logs
                  </div>
                  <div style={{ fontSize: '14px', color: colors.text }}>
                    {logsInfo.total} baris total
                    {logsInfo.eligibleForCleanup > 0 && (
                      <span style={{ color: colors.textDim }}> &middot; {logsInfo.eligibleForCleanup} lebih tua dari {logsInfo.olderThanDays} hari</span>
                    )}
                  </div>
                  {cleanupMessage && (
                    <div style={{ fontSize: '13px', color: colors.long, marginTop: '4px' }}>{cleanupMessage}</div>
                  )}
                </div>
                <button
                  onClick={handleCleanupLogs}
                  disabled={cleaningUp || logsInfo.eligibleForCleanup === 0}
                  style={{
                    padding: '9px 16px', borderRadius: '6px', border: 'none', fontWeight: 600, fontSize: '13px',
                    background: logsInfo.eligibleForCleanup === 0 ? colors.border : colors.dangerBg,
                    color: logsInfo.eligibleForCleanup === 0 ? colors.textFaint : colors.short,
                    cursor: cleaningUp || logsInfo.eligibleForCleanup === 0 ? 'not-allowed' : 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {cleaningUp ? 'Menghapus...' : `Hapus Log Lama (>${logsInfo.olderThanDays}h)`}
                </button>
              </div>
            )}

            <h2 style={{ fontSize: '15px', color: colors.textDim, textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 12px', fontWeight: 600 }}>
              Recent Signals
            </h2>

            <div style={{ background: colors.panel, border: `1px solid ${colors.border}`, borderRadius: '10px', overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: mono, fontSize: '13px' }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                      <th style={thStyle}>Time</th>
                      <th style={thStyle}>Type</th>
                      <th style={thStyle}>TF</th>
                      <th style={thStyle}>Score</th>
                      <th style={thStyle}>Entry</th>
                      <th style={thStyle}>SL</th>
                      <th style={thStyle}>TP1</th>
                      <th style={thStyle}>TP2</th>
                      <th style={thStyle}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {signals.length === 0 && !errorMessage && (
                      <tr><td colSpan={9} style={{ padding: '24px', textAlign: 'center', color: colors.textFaint, fontFamily: sans }}>Belum ada signal.</td></tr>
                    )}
                    {signals.map((signal) => {
                      const badge = statusStyle(signal.status);
                      return (
                        <tr key={signal.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                          <td style={{ ...tdStyle, color: colors.textDim }}>{new Date(signal.createdAt).toLocaleString()}</td>
                          <td style={{ ...tdStyle, color: signal.type === 'LONG' ? colors.long : colors.short, fontWeight: 600 }}>
                            {signal.type}
                          </td>
                          <td style={tdStyle}>{signal.timeframe}</td>
                          <td style={tdStyle}>{signal.score}</td>
                          <td style={tdStyle}>
                            {signal.entryZoneStart.toFixed(0)}-{signal.entryZoneEnd.toFixed(0)}
                          </td>
                          <td style={tdStyle}>{signal.stopLoss.toFixed(0)}</td>
                          <td style={tdStyle}>{signal.tp1.toFixed(0)}</td>
                          <td style={tdStyle}>{signal.tp2.toFixed(0)}</td>
                          <td style={tdStyle}>
                            <span style={{
                              background: badge.bg, color: badge.color, padding: '2px 8px',
                              borderRadius: '999px', fontSize: '11px', fontFamily: sans, fontWeight: 600,
                            }}>
                              {signal.status}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {pagination && pagination.total > pagination.limit && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', marginTop: '16px' }}>
                <button
                  onClick={() => changeSignalsPage(pagination.page - 1)}
                  disabled={signalsLoading || pagination.page <= 1}
                  style={{
                    padding: '8px 14px', borderRadius: '6px', border: `1px solid ${colors.border}`,
                    background: 'transparent', color: pagination.page <= 1 ? colors.textFaint : colors.text,
                    fontSize: '13px', fontFamily: sans,
                    cursor: signalsLoading || pagination.page <= 1 ? 'not-allowed' : 'pointer',
                  }}
                >
                  &laquo; Sebelumnya
                </button>
                <span style={{ color: colors.textDim, fontSize: '13px', fontFamily: mono }}>
                  {signalsLoading
                    ? 'Memuat...'
                    : `Halaman ${pagination.page} dari ${Math.max(1, Math.ceil(pagination.total / pagination.limit))} \u00b7 ${pagination.total} signal`}
                </span>
                <button
                  onClick={() => changeSignalsPage(pagination.page + 1)}
                  disabled={signalsLoading || pagination.page * pagination.limit >= pagination.total}
                  style={{
                    padding: '8px 14px', borderRadius: '6px', border: `1px solid ${colors.border}`,
                    background: 'transparent',
                    color: pagination.page * pagination.limit >= pagination.total ? colors.textFaint : colors.text,
                    fontSize: '13px', fontFamily: sans,
                    cursor: signalsLoading || pagination.page * pagination.limit >= pagination.total ? 'not-allowed' : 'pointer',
                  }}
                >
                  Selanjutnya &raquo;
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default App;
